/**
 * @file Icones SVG generees (aucun fichier image) : monnaies/ressources, silhouettes d'armes, emoji de comportement.
 */
import type { AmmoShape, AmmoStyle, AmmoTrail, AuraParticle, AuraStyle, FireworkStyle, GunSilhouette, ShotFxStyle } from '@config/types';
import type { GradeDef, WeaponDef } from '@config/types';
import { h } from './dom';
import { weaponIconScale, weaponIconSpec } from './gunSpec';
import type { WalletId, WeaponBehavior } from '@shared/enums';
import { uiIcon } from './uiIcons';
import type { UiIconId } from './uiIcons';

const NS = 'http://www.w3.org/2000/svg';
const STROKE = '#2b2017';

type SvgAttrs = Record<string, string | number>;

const s = (tag: string, attrs: SvgAttrs = {}, ...kids: SVGElement[]): SVGElement => {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  for (const c of kids) e.append(c);
  return e;
};

const root = (viewBox: string, cls: string, ...kids: SVGElement[]): SVGSVGElement => {
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', viewBox);
  svg.setAttribute('class', cls);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  for (const k of kids) svg.append(k);
  return svg;
};

const outline = (fill: string, extra: SvgAttrs = {}): SvgAttrs => ({ fill, stroke: STROKE, 'stroke-width': 2.5, 'stroke-linejoin': 'round', 'stroke-linecap': 'round', ...extra });

const gem = (fill: string, shine: string): SVGElement[] => [
  s('polygon', outline(fill, { points: '16,3 28,12 16,29 4,12' })),
  s('polyline', { points: '4,12 28,12', fill: 'none', stroke: STROKE, 'stroke-width': 1.6 }),
  s('polyline', { points: '11,12 16,3 21,12 16,29 11,12', fill: 'none', stroke: STROKE, 'stroke-width': 1.2, opacity: 0.55 }),
  s('polygon', { points: '9,11 12,6 15,11', fill: shine, opacity: 0.8 }),
];

const hex = (fill: string): SVGElement[] => [
  s('polygon', outline(fill, { points: '16,3 27,9.5 27,22.5 16,29 5,22.5 5,9.5' })),
  s('circle', { cx: 16, cy: 16, r: 4.5, fill: '#fff', opacity: 0.6 }),
];

const ore = (fill: string): SVGElement[] => [
  s('polygon', outline('#b9b2a8', { points: '4,26 7,12 15,6 25,9 29,20 24,28' })),
  s('circle', outline(fill, { cx: 13, cy: 17, r: 4.2 })),
  s('circle', outline(fill, { cx: 21, cy: 21, r: 3.4 })),
];

/** Icone d'une monnaie/ressource (carre 32x32), couleur propre a chaque identifiant. */
export const currencyIcon = (id: WalletId, cls = 'ico'): SVGSVGElement => {
  let kids: SVGElement[];
  switch (id) {
    case 'money':
      kids = [
        s('rect', outline('#8ee07a', { x: 2.5, y: 7, width: 27, height: 18, rx: 4 })),
        s('circle', outline('#d6f7c9', { cx: 16, cy: 16, r: 5.2 })),
        s('circle', { cx: 7, cy: 16, r: 1.6, fill: STROKE }),
        s('circle', { cx: 25, cy: 16, r: 1.6, fill: STROKE }),
      ];
      break;
    case 'dia':
      kids = gem('#7fd3ff', '#ffffff');
      break;
    case 'ruby':
      kids = gem('#ff6f91', '#ffe3ea');
      break;
    case 'starGem':
      kids = [s('polygon', outline('#ffd25e', { points: '16,2.5 20,11.5 29.5,12.4 22.4,18.9 24.5,28.5 16,23.6 7.5,28.5 9.6,18.9 2.5,12.4 12,11.5' })), s('circle', { cx: 13, cy: 12, r: 1.6, fill: '#fff', opacity: 0.9 })];
      break;
    case 'petCoin':
      kids = [
        s('circle', outline('#ffb86b', { cx: 16, cy: 16, r: 12.5 })),
        s('circle', { cx: 16, cy: 18, r: 4.2, fill: STROKE }),
        s('circle', { cx: 10.5, cy: 12, r: 1.9, fill: STROKE }),
        s('circle', { cx: 16, cy: 9.6, r: 1.9, fill: STROKE }),
        s('circle', { cx: 21.5, cy: 12, r: 1.9, fill: STROKE }),
      ];
      break;
    case 'coreForest':
      kids = hex('#7fd16a');
      break;
    case 'coreDesert':
      kids = hex('#f0c36d');
      break;
    case 'coreTundra':
      kids = hex('#a8dcff');
      break;
    case 'wood':
      kids = [s('rect', outline('#b07d4f', { x: 3, y: 9, width: 26, height: 15, rx: 4 })), s('ellipse', outline('#e8c9a0', { cx: 26, cy: 16.5, rx: 3.2, ry: 6 })), s('path', { d: 'M8 14h9M8 19h12', stroke: STROKE, 'stroke-width': 1.4, fill: 'none', opacity: 0.55 })];
      break;
    case 'stone':
      kids = [s('polygon', outline('#b9b2a8', { points: '4,26 7,12 15,6 25,9 29,20 24,28' }))];
      break;
    case 'ironOre':
      kids = ore('#8d97a6');
      break;
    case 'copperOre':
      kids = ore('#d98b4e');
      break;
    case 'silverOre':
      kids = ore('#e4e9ef');
      break;
    case 'goldOre':
      kids = ore('#ffd25e');
      break;
    case 'crystal':
      kids = gem('#c79bff', '#f3e6ff');
      break;
  }
  return root('0 0 32 32', cls, ...kids);
};

type GunShape = { body: [number, number, number, number]; barrel: [number, number, number, number]; grip?: [number, number, number, number]; stock?: boolean; mag?: boolean; scope?: boolean; fat?: boolean };

const SHAPES: Record<GunSilhouette, GunShape> = {
  pistol: { body: [8, 7, 20, 7], barrel: [28, 8, 10, 5], grip: [10, 14, 6, 10] },
  revolver: { body: [8, 6, 20, 9], barrel: [28, 8, 12, 5], grip: [9, 15, 7, 9] },
  smg: { body: [6, 7, 26, 8], barrel: [32, 8.5, 10, 4.5], grip: [10, 15, 6, 9], mag: true },
  shotgun: { body: [8, 8, 18, 6], barrel: [26, 8, 18, 5], stock: true },
  doubleShotgun: { body: [8, 8, 16, 6], barrel: [24, 6.5, 20, 4], stock: true },
  pumpShotgun: { body: [8, 8, 18, 6], barrel: [26, 8, 18, 5], grip: [24, 13, 8, 4], stock: true },
  drumShotgun: { body: [8, 8, 18, 6], barrel: [26, 8, 18, 5], stock: true, mag: true },
  rifle: { body: [8, 8, 22, 6], barrel: [30, 9, 14, 3.6], grip: [12, 14, 5, 8], stock: true },
  boltRifle: { body: [8, 8, 22, 5], barrel: [30, 9, 16, 3], stock: true, scope: true },
  railgun: { body: [6, 7, 24, 8], barrel: [30, 8.5, 16, 5], scope: true, fat: true },
  minigun: { body: [6, 8, 16, 9], barrel: [22, 7, 22, 11], grip: [10, 17, 6, 7] },
  gatling: { body: [6, 8, 14, 9], barrel: [20, 6.5, 24, 12], grip: [9, 17, 6, 7] },
  cannon: { body: [6, 7, 20, 11], barrel: [26, 6, 18, 13], fat: true },
  grenadeLauncher: { body: [8, 8, 18, 8], barrel: [26, 7, 14, 10], grip: [11, 16, 6, 8], fat: true },
  sniper: { body: [6, 8, 24, 5], barrel: [30, 9, 16, 3], stock: true, scope: true },
  launcher: { body: [4, 8, 38, 11], barrel: [38, 6, 6, 15], fat: true },
  energyCannon: { body: [6, 6, 22, 13], barrel: [28, 8, 16, 9], fat: true, scope: true },
};

/** Silhouette d'arme stylisee (48x28) aux couleurs du grade. */
export const gunIcon = (silhouette: GunSilhouette, primary: string, secondary: string, cls = 'gun-ico'): SVGSVGElement => {
  const sh = SHAPES[silhouette];
  const r = (a: [number, number, number, number], fill: string, rx = 1.5): SVGElement => s('rect', outline(fill, { x: a[0], y: a[1], width: a[2], height: a[3], rx }));
  const kids: SVGElement[] = [];
  if (sh.stock) kids.push(s('polygon', outline(secondary, { points: `${sh.body[0] + 1},${sh.body[1] + 1} 2,${sh.body[1] + 6} 2,${sh.body[1] + 11} ${sh.body[0] + 3},${sh.body[1] + 6}` })));
  if (sh.grip) kids.push(r(sh.grip, secondary, 2));
  if (sh.mag) kids.push(r([sh.body[0] + 10, sh.body[1] + sh.body[3] - 1, 6, 9], secondary, 2));
  kids.push(r(sh.barrel, secondary, sh.fat ? 3 : 1.2));
  kids.push(r(sh.body, primary, 2.5));
  if (sh.scope) kids.push(r([sh.body[0] + 6, sh.body[1] - 4, 11, 3.5], '#4a5568', 1.5));
  return root('0 0 48 28', cls, ...kids);
};

/** Icone detaillee d'une arme precise (65 icones differentes : voir gunSpec.ts), aux couleurs de son grade. */
export const weaponIcon = (def: WeaponDef, grade: GradeDef, cls = 'gun-ico'): SVGSVGElement => {
  const kids = weaponIconSpec(def, grade).map((p) => s(p.tag, p.stroke ? outline(String(p.attrs['fill']), { ...p.attrs, 'stroke-width': 2 }) : p.attrs));
  const k = weaponIconScale(def);
  const g = s('g', { transform: `translate(24 14) scale(${k}) translate(-24 -14)` }, ...kids);
  return root('0 0 48 28', cls, g);
};

/** Icone SVG (uiIcons) d'un comportement d'arme : badge des vignettes et titres de l'onglet Munitions. */
export const BEHAVIOR_ICON: Record<WeaponBehavior, UiIconId> = {
  single: 'dot',
  pierce: 'pierce',
  shotgun: 'shotgun',
  explosive: 'power',
  laser: 'bolt',
  missile: 'missile',
  blastSniper: 'comet',
};

/** Petit emoji representant un comportement d'arme (conserve pour la galerie de dev). */
export const BEHAVIOR_EMOJI: Record<WeaponBehavior, string> = {
  single: '•',
  pierce: '➶',
  shotgun: '⁙',
  explosive: '💥',
  laser: '⚡',
  missile: '🚀',
  blastSniper: '☄️',
};

// ---------------------------------------------------------------------------------------------------------------------
// Apercu d'un skin de munition : traine (points selon le style) + forme de la munition, en SVG 72x32
// ---------------------------------------------------------------------------------------------------------------------

const RAINBOW = ['#ff6b6b', '#ffb454', '#ffe066', '#7be08a', '#5cc8ff', '#a78bff'];

/** Forme de la munition centree en (cx, cy), taille ~ r. */
const ammoShape = (shape: AmmoShape, cx: number, cy: number, r: number, c1: string, c2: string): SVGElement[] => {
  switch (shape) {
    case 'capsule':
      return [s('rect', outline(c1, { x: cx - r * 1.5, y: cy - r * 0.55, width: r * 3, height: r * 1.1, rx: r * 0.55 })), s('rect', { x: cx - r * 0.9, y: cy - r * 0.3, width: r * 1.8, height: r * 0.3, rx: r * 0.15, fill: c2, opacity: 0.85 })];
    case 'orb':
      return [s('circle', outline(c1, { cx, cy, r })), s('circle', { cx: cx - r * 0.3, cy: cy - r * 0.3, r: r * 0.32, fill: c2, opacity: 0.9 })];
    case 'star': {
      const pts: string[] = [];
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        const rr = i % 2 === 0 ? r * 1.25 : r * 0.55;
        pts.push(`${(cx + Math.cos(a) * rr).toFixed(1)},${(cy + Math.sin(a) * rr).toFixed(1)}`);
      }
      return [s('polygon', outline(c1, { points: pts.join(' ') })), s('circle', { cx, cy, r: r * 0.25, fill: c2, opacity: 0.9 })];
    }
    case 'heart':
      return [s('path', outline(c1, { d: `M${cx},${cy + r * 0.95} C${cx - r * 1.6},${cy - r * 0.1} ${cx - r * 0.8},${cy - r * 1.1} ${cx},${cy - r * 0.35} C${cx + r * 0.8},${cy - r * 1.1} ${cx + r * 1.6},${cy - r * 0.1} ${cx},${cy + r * 0.95} Z` })), s('circle', { cx: cx - r * 0.45, cy: cy - r * 0.3, r: r * 0.2, fill: c2, opacity: 0.9 })];
    case 'fish':
      return [
        s('polygon', outline(c2, { points: `${cx - r * 0.9},${cy} ${cx - r * 1.7},${cy - r * 0.75} ${cx - r * 1.7},${cy + r * 0.75}` })),
        s('ellipse', outline(c1, { cx, cy, rx: r * 1.1, ry: r * 0.75 })),
        s('circle', { cx: cx + r * 0.45, cy: cy - r * 0.15, r: r * 0.17, fill: STROKE }),
      ];
    case 'diamond':
      return [s('polygon', outline(c1, { points: `${cx},${cy - r * 1.2} ${cx + r * 0.85},${cy} ${cx},${cy + r * 1.2} ${cx - r * 0.85},${cy}` })), s('polygon', { points: `${cx - r * 0.3},${cy - r * 0.2} ${cx},${cy - r * 0.8} ${cx + r * 0.1},${cy - r * 0.1}`, fill: c2, opacity: 0.85 })];
  }
};

/** Points de traine derriere la munition (a gauche), selon le style de traine. */
const ammoTrail = (trail: AmmoTrail, cy: number, c1: string, c2: string, rainbow: boolean): SVGElement[] => {
  const out: SVGElement[] = [];
  const n = 5;
  for (let i = 0; i < n; i++) {
    const x = 36 - i * 7.5;
    const k = 1 - i / (n + 0.5);
    const jitter = Math.sin(i * 2.3) * 3;
    const col = rainbow || trail === 'rainbow' ? RAINBOW[i % RAINBOW.length] ?? c1 : i % 2 === 0 ? c1 : c2;
    switch (trail) {
      case 'none':
        return [];
      case 'smoke':
        out.push(s('circle', { cx: x, cy: cy + jitter * 0.4, r: 2 + (1 - k) * 4, fill: '#c9ced6', opacity: 0.8 * k }));
        break;
      case 'sparkle':
        out.push(s('path', { d: `M${x},${cy + jitter - 3.5 * k} L${x + 1},${cy + jitter - 1} L${x + 3.5 * k},${cy + jitter} L${x + 1},${cy + jitter + 1} L${x},${cy + jitter + 3.5 * k} L${x - 1},${cy + jitter + 1} L${x - 3.5 * k},${cy + jitter} L${x - 1},${cy + jitter - 1} Z`, fill: col, opacity: 0.4 + 0.6 * k }));
        break;
      case 'embers':
        out.push(s('circle', { cx: x, cy: cy - (1 - k) * 6 + jitter * 0.3, r: 1.2 + 1.8 * k, fill: i % 2 === 0 ? c1 : c2, opacity: 0.5 + 0.5 * k }));
        break;
      case 'bubbles':
        out.push(s('circle', { cx: x, cy: cy + jitter, r: 1.6 + 1.6 * (1 - k), fill: 'none', stroke: col, 'stroke-width': 1.3, opacity: 0.4 + 0.6 * k }));
        break;
      case 'hearts':
        out.push(s('path', { d: `M${x},${cy + jitter + 2.5 * k} C${x - 4 * k},${cy + jitter - 0.5} ${x - 2 * k},${cy + jitter - 3.5 * k} ${x},${cy + jitter - 1} C${x + 2 * k},${cy + jitter - 3.5 * k} ${x + 4 * k},${cy + jitter - 0.5} ${x},${cy + jitter + 2.5 * k} Z`, fill: col, opacity: 0.45 + 0.55 * k }));
        break;
      case 'petals':
        out.push(s('ellipse', { cx: x, cy: cy + jitter, rx: 3 * k + 1, ry: 1.6 * k + 0.6, fill: col, opacity: 0.5 + 0.5 * k, transform: `rotate(${i * 40 - 20} ${x} ${cy + jitter})` }));
        break;
      case 'snow':
        out.push(s('circle', { cx: x, cy: cy + jitter, r: 1.2 + 1.4 * k, fill: '#ffffff', stroke: c1, 'stroke-width': 0.8, opacity: 0.5 + 0.5 * k }));
        break;
      case 'rainbow':
        out.push(s('circle', { cx: x, cy: cy + jitter * 0.4, r: 2.4 * k + 0.8, fill: col, opacity: 0.55 + 0.45 * k }));
        break;
    }
  }
  return out;
};

/** Apercu d'un skin de munition (SVG 72x32) : traine + forme ; `rainbow` / `glow` pilotes par des classes CSS (teinte qui cycle, halo). */
export const ammoPreview = (style: AmmoStyle, cls = 'ammo-ico'): SVGSVGElement => {
  const [c1, c2] = style.colors;
  const svg = root('0 0 72 32', `${cls}${style.glow ? ' glow' : ''}${style.rainbow ? ' rainbow' : ''}`, ...ammoTrail(style.trail, 16, c1, c2, style.rainbow === true), ...ammoShape(style.shape, 52, 16, 6.5 * Math.min(1.3, style.size), c1, c2));
  svg.style.setProperty('--ammo', c1);
  return svg;
};

/** Particules d'apercu d'aura en SVG (l'emoji 🫧 n'existe pas sur les Android anterieurs a 12 : carre vide). */
const AURA_ICON: Record<AuraParticle, UiIconId> = { dot: 'sparkle', star: 'star', heart: 'heart', flame: 'flame', snow: 'snow', petal: 'petal', note: 'note', bubble: 'bubble', bolt: 'bolt' };

/** Apercu d'une aura : disque lumineux aux couleurs de l'aura + chat + quelques particules SVG ; classes `rainbow` / `pillar` pour les auras spectaculaires. */
export const auraPreview = (style: AuraStyle): HTMLElement => {
  const [c1, c2] = style.colors;
  const el = h('div', { class: `aura-ico${style.rainbow ? ' rainbow' : ''}${style.pillar ? ' pillar' : ''}${style.groundRing ? ' ring' : ''}` });
  el.style.setProperty('--aura1', c1);
  el.style.setProperty('--aura2', c2);
  el.append(h('span', { class: 'aura-cat' }, uiIcon('cat')));
  const n = 2 + style.density;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const p = h('span', { class: 'aura-p' }, uiIcon(AURA_ICON[style.particle]));
    p.style.setProperty('--x', `${Math.round(50 + Math.cos(a) * 38)}%`);
    p.style.setProperty('--y', `${Math.round(50 + Math.sin(a) * 34)}%`);
    p.style.setProperty('--d', `${(i * 0.37).toFixed(2)}s`);
    el.append(p);
  }
  return el;
};

/** Points (x, y, rayon relatif) d'un feu d'artifice sur une grille 100x72, selon son motif. */
const burstPoints = (pattern: FireworkStyle['pattern'], size: number): Array<[number, number, number]> => {
  const cx = 50;
  const cy = 34;
  const r = 14 + size * 5;
  const pts: Array<[number, number, number]> = [];
  const ring = (n: number, rr: number, rot = 0): void => {
    for (let i = 0; i < n; i++) {
      const a = rot + (i / n) * Math.PI * 2;
      pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr, 1]);
    }
  };
  switch (pattern) {
    case 'ring':
      ring(16, r);
      break;
    case 'peony':
      ring(14, r);
      ring(10, r * 0.6, 0.3);
      pts.push([cx, cy, 1.4]);
      break;
    case 'double':
      ring(14, r);
      ring(10, r * 0.5);
      break;
    case 'heart':
      for (let i = 0; i < 22; i++) {
        const t = (i / 22) * Math.PI * 2;
        pts.push([cx + (16 * Math.sin(t) ** 3 * r) / 17, cy - ((13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)) * r) / 17, 1]);
      }
      break;
    case 'star':
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i / 10) * Math.PI * 2;
        const rr = i % 2 === 0 ? r : r * 0.45;
        for (let k = 1; k <= 3; k++) pts.push([cx + Math.cos(a) * rr * (k / 3), cy + Math.sin(a) * rr * (k / 3), 1]);
      }
      break;
    case 'spiral':
      for (let i = 0; i < 26; i++) {
        const a = i * 0.55;
        const rr = (i / 26) * r;
        pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr, 1]);
      }
      break;
    case 'saturn':
      ring(10, r * 0.55);
      for (let i = 0; i < 18; i++) {
        const a = (i / 18) * Math.PI * 2;
        pts.push([cx + Math.cos(a) * r * 1.15, cy + Math.sin(a) * r * 0.3, 1]);
      }
      break;
    case 'crossette':
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const x = cx + Math.cos(a) * r * 0.8;
        const y = cy + Math.sin(a) * r * 0.8;
        pts.push([x, y, 1.2], [x + 4, y, 0.8], [x - 4, y, 0.8], [x, y + 4, 0.8], [x, y - 4, 0.8]);
      }
      break;
    case 'willow':
    case 'palm':
      for (let i = 0; i < 9; i++) {
        const a = -Math.PI * 0.95 + (i / 8) * Math.PI * 0.9;
        for (let k = 1; k <= 4; k++) {
          const t = k / 4;
          pts.push([cx + Math.cos(a) * r * t, cy + Math.sin(a) * r * t + t * t * r * 0.7, 1 - t * 0.3]);
        }
      }
      break;
    case 'cascade':
      for (let j = -1; j <= 1; j++) {
        for (let k = 0; k < 8; k++) {
          const t = k / 7;
          pts.push([cx + j * r * 0.7 + (t - 0.5) * j * 8, cy - r * 0.3 + t * r * 1.1 - Math.sin(t * Math.PI) * r * 0.5, 1 - t * 0.4]);
        }
      }
      break;
  }
  return pts;
};

/** Apercu d'un feu d'artifice (SVG 100x72 sur fond nuit) ; `rainbow` pilote par classe CSS (teinte qui cycle). */
export const fireworkPreview = (style: FireworkStyle, ammo?: AmmoStyle): SVGSVGElement => {
  const [c1, c2] = style.useAmmo && ammo ? ammo.colors : style.colors;
  const dots = burstPoints(style.pattern, style.size).map(([x, y, k], i) => s('circle', { cx: x.toFixed(1), cy: y.toFixed(1), r: (1.8 * k).toFixed(1), fill: i % 3 === 0 ? c2 : c1 }));
  const stem = style.trail ? [s('path', { d: 'M50 70 Q48 56 50 40', stroke: c2, 'stroke-width': 1.6, fill: 'none', 'stroke-linecap': 'round', opacity: 0.6, 'stroke-dasharray': '2 3' })] : [];
  const svg = root('0 0 100 72', `fw-ico${style.rainbow ? ' rainbow' : ''}`, ...stem, ...dots);
  svg.style.setProperty('--fw', c1);
  return svg;
};

/** Apercu d'un FX de tir (SVG 100x72) : boule d'impact aux couleurs du FX, rayons, anneaux et quelques particules ; `rainbow` pilote par classe CSS. */
export const shotFxPreview = (style: ShotFxStyle): SVGSVGElement => {
  const [c0, c1, c2] = style.colors;
  const k = Math.min(1.25, 0.75 + style.scale * 0.25);
  const rays: SVGElement[] = [];
  const n = 10 + style.impact.length * 2;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const r0 = 11 * k;
    const r1 = (24 + (i % 2) * 9) * k;
    rays.push(s('line', { x1: (50 + Math.cos(a) * r0).toFixed(1), y1: (36 + Math.sin(a) * r0 * 0.8).toFixed(1), x2: (50 + Math.cos(a) * r1).toFixed(1), y2: (36 + Math.sin(a) * r1 * 0.8).toFixed(1), stroke: i % 3 === 0 ? c2 : c1, 'stroke-width': 2.4, 'stroke-linecap': 'round' }));
  }
  const bits: SVGElement[] = [];
  for (let i = 0; i < 6; i++) {
    bits.push(s('circle', { cx: (50 + Math.cos(i * 1.7 + 0.4) * (30 + (i % 3) * 5) * k).toFixed(1), cy: (36 + Math.sin(i * 1.7 + 0.4) * (22 + (i % 2) * 5) * k * 0.8).toFixed(1), r: 1.6 + (i % 3) * 0.7, fill: i % 2 ? c2 : c0 }));
  }
  const svg = root('0 0 100 72', `shotfx-ico${style.rainbow ? ' rainbow' : ''}`,
    s('ellipse', { cx: 50, cy: 38, rx: 34 * k, ry: 12 * k, fill: 'none', stroke: c1, 'stroke-width': 2, opacity: 0.55 }),
    ...rays,
    s('circle', { cx: 50, cy: 36, r: 15 * k, fill: c1, opacity: 0.55 }),
    s('circle', { cx: 50, cy: 36, r: 10 * k, fill: c0 }),
    s('circle', { cx: 50, cy: 36, r: 4.5 * k, fill: '#fff' }),
    ...bits,
  );
  svg.style.setProperty('--fw', c0);
  return svg;
};
